import { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  ActionIcon, Alert, Anchor, Avatar, Badge, Box, Button, Card, Center, Chip, CloseButton, Grid, Group, Loader, Menu, Modal,
  Select, Stack, Tabs, Text, Textarea, TextInput, ThemeIcon, Title, Tooltip, UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { Dropzone } from "@mantine/dropzone";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  IconAlertTriangle, IconArrowLeft, IconBriefcase, IconBuilding, IconChevronDown,
  IconChevronRight, IconDownload, IconFileText, IconFolder, IconGripVertical, IconListCheck, IconPencil, IconPlus,
  IconPlayerPause, IconPlayerPlay, IconRestore, IconSettings, IconTrash, IconUpload, IconUser, IconUserPlus, IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  getProject, getClient, getProjectCandidates, getCandidate,
  addProjectCandidate, updateProjectCandidate, updateProject,
  deleteProjectCandidate,
  getGeneratedDocuments, deleteGeneratedDocument, getProjectFiles, deleteProjectFile,
} from "../api";
import { downloadFile, getAuthHeaders } from "../../../Boilerplate/api";
import ProjectCandidateDrawer from "./ProjectCandidateDrawer";
import ProjectStatusTab from "./ProjectStatusTab";
import CandidatePicker from "../components/CandidatePicker";
import AddCandidateDialog from "../components/AddCandidateDialog";
import InfoHint from "../components/InfoHint";
import ScrollableTabsList from "../components/ScrollableTabsList";
import { useIsMobile } from "../hooks/useIsMobile";
import TasksPanel from "../Tasks/TasksPanel";
import { alphaDisplayName, candidateAlphaHaystack, compareCandidatesAlpha, sortIdsByCandidateAlpha } from "../_Shared/candidateAlphaSort";
import { candidateRoleAndCompany } from "../_Shared/candidateRole";
import CompanyLink from "../components/CompanyLink";
import { projectStatusColor, projectStatusLabel, projectStatusOptions } from "../_Shared/projectStatus";
import { formatDateTime } from "../_Shared/time";
import DocumentGenerator, { GeneratedDocumentsList } from "../Documents/DocumentGenerator";

const stageKeys = [
  { key: "identified", tKey: "stage_identified", color: "gray" },
  { key: "longlist", tKey: "stage_longlist", color: "gray" },
  { key: "shortlist", tKey: "stage_shortlist", color: "blue" },
  { key: "interview", tKey: "stage_interview", color: "violet" },
  { key: "finalist", tKey: "stage_finalist", color: "green" },
  { key: "placed", tKey: "stage_placed", color: "teal" },
];

const rankingOptions = [
  { value: "", label: "" },
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "C", label: "C" },
];

const initiatorColors = { candidate: "orange", consultant: "gray", client: "red" };
const initiatorValues = ["consultant", "candidate", "client"];

function RejectedByBadge({ pc, onSelect, t }) {
  const [saving, setSaving] = useState(false);
  async function pick(value) {
    if (!value || value === pc.rejectedBy) return;
    setSaving(true);
    try {
      await onSelect(pc, value);
    } finally {
      setSaving(false);
    }
  }
  const assigned = Boolean(pc.rejectedBy);
  return (
    <Menu shadow="sm" position="bottom-start" withinPortal disabled={saving}>
      <Menu.Target>
        <UnstyledButton
          aria-label={t("reject_who_initiated")}
          disabled={saving}
          style={{ display: "inline-flex" }}
        >
          <Badge
            size="xs"
            variant="light"
            color={assigned ? (initiatorColors[pc.rejectedBy] ?? "gray") : "orange"}
            style={{ cursor: saving ? "wait" : "pointer" }}
          >
            {saving
              ? "…"
              : assigned
                ? t(`rejection_${pc.rejectedBy}`)
                : t("rejected_filter_unattributed")}
          </Badge>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {initiatorValues.map((value) => (
          <Menu.Item
            key={value}
            disabled={pc.rejectedBy === value}
            onClick={() => pick(value)}
          >
            {t(`rejection_${value}`)}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

const verdictColors = { go: "var(--mantine-color-green-6)", noGo: "var(--mantine-color-red-6)" };

function RoleCompanyLine({ candidate, t, size = "xs" }) {
  const { role, company, companyId, isFormer } = candidateRoleAndCompany(candidate);
  if (!role) return null;
  const roleLabel = isFormer ? `${t("role_former_prefix")} ${role}` : role;
  return (
    <Text size={size} c="dimmed" truncate>
      {roleLabel}
      {company ? " · " : ""}
      <CompanyLink company={company} companyId={companyId} />
    </Text>
  );
}

function CandidateCard({ pc, candidateData, onOpenDrawer, onHold, onReject, dragHandleProps }) {
  const { t } = useTranslation("Veft");
  const candidate = candidateData ?? {};
  const verdictColor = verdictColors[pc.verdict] ?? null;
  const name = alphaDisplayName(candidate);

  return (
    <div style={{
      padding: "6px 8px",
      borderRadius: "var(--mantine-radius-sm)",
      backgroundColor: "var(--mantine-color-default)",
      border: "1px solid var(--mantine-color-default-border)",
      borderLeft: verdictColor ? `3px solid ${verdictColor}` : "1px solid var(--mantine-color-default-border)",
    }}>
      <Group gap={6} wrap="nowrap">
        <div {...dragHandleProps} style={{ cursor: "grab", display: "flex", alignItems: "center", flexShrink: 0 }}>
          <IconGripVertical size={12} color="var(--mantine-color-dimmed)" />
        </div>
        <div
          style={{ cursor: "pointer", flex: 1, minWidth: 0 }}
          onClick={() => onOpenDrawer(pc)}
        >
          <Text size="xs" fw={500} truncate>{name || "Loading..."}</Text>
          <RoleCompanyLine candidate={candidate} t={t} />
        </div>
        <Tooltip label={t("put_on_hold")} openDelay={400}>
          <IconPlayerPause
            size={12}
            color="var(--mantine-color-yellow-7)"
            style={{ cursor: "pointer", flexShrink: 0 }}
            aria-label={t("put_on_hold")}
            onClick={(e) => { e.stopPropagation(); onHold(pc); }}
          />
        </Tooltip>
        <IconX
          size={12}
          color="var(--mantine-color-red-6)"
          style={{ cursor: "pointer", flexShrink: 0 }}
          onClick={(e) => { e.stopPropagation(); onReject(pc); }}
        />
      </Group>
    </div>
  );
}

function CollapsibleBriefing({ text, maxLines = 4 }) {
  const { t } = useTranslation("Veft");
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflow, setOverflow] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    const measure = () => setOverflow(el.scrollHeight > el.clientHeight + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, expanded]);

  return (
    <div style={{ marginBottom: "var(--mantine-spacing-md)" }}>
      <Text
        ref={ref}
        size="sm"
        c="dimmed"
        style={{
          whiteSpace: "pre-wrap",
          ...(expanded ? null : {
            display: "-webkit-box",
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }),
        }}
      >
        {text}
      </Text>
      {(overflow || expanded) && (
        <Anchor
          component="button"
          type="button"
          size="xs"
          onClick={() => setExpanded((v) => !v)}
          mt={4}
        >
          {expanded ? t("show_less") : t("show_more")}
        </Anchor>
      )}
    </div>
  );
}

function ProjectSettings({ project, onUpdate }) {
  const { t } = useTranslation("Veft");
  const [dimensions, setDimensions] = useState(project.competencyDimensions ?? []);
  const [observations, setObservations] = useState(project.searchObservations ?? "");
  const [newDim, setNewDim] = useState("");
  const [saving, setSaving] = useState(false);

  // Keep local state in sync if the upstream project changes (e.g., another
  // edit elsewhere in the app updates competencyDimensions).
  useEffect(() => {
    setDimensions(project.competencyDimensions ?? []);
  }, [project.competencyDimensions]);

  async function save(updates) {
    setSaving(true);
    try {
      const updated = await updateProject(project.id, updates);
      onUpdate(updated);
      notifications.show({ title: t("saved"), message: t("project_updated"), color: "green" });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    } finally {
      setSaving(false);
    }
  }

  function addDimension() {
    if (!newDim.trim()) return;
    const updated = [...dimensions, { name: newDim.trim() }];
    setDimensions(updated);
    setNewDim("");
    save({ competencyDimensions: updated });
  }

  function removeDimension(index) {
    const updated = dimensions.filter((_, i) => i !== index);
    setDimensions(updated);
    save({ competencyDimensions: updated });
  }

  function setDimDescription(index, value) {
    setDimensions((prev) => prev.map((d, i) => (i === index ? { ...d, description: value } : d)));
  }

  function setDimName(index, value) {
    setDimensions((prev) => prev.map((d, i) => (i === index ? { ...d, name: value } : d)));
  }

  function commitDescription(index) {
    const upstream = (project.competencyDimensions ?? [])[index]?.description ?? "";
    const local = dimensions[index]?.description ?? "";
    if (upstream === local) return;
    save({ competencyDimensions: dimensions });
  }

  function commitName(index) {
    const upstream = (project.competencyDimensions ?? [])[index]?.name ?? "";
    const local = dimensions[index]?.name?.trim() ?? "";
    if (!local) {
      // Empty name: revert to upstream (user can use × to delete instead).
      setDimensions((prev) => prev.map((d, i) => (i === index ? { ...d, name: upstream } : d)));
      return;
    }
    if (upstream === local) return;
    // Apply trimmed value locally and persist as a rename so existing scores
    // and assessment-section narratives are carried over instead of orphaned.
    const next = dimensions.map((d, i) => (i === index ? { ...d, name: local } : d));
    setDimensions(next);
    save({
      competencyDimensions: next,
      dimensionRenames: [{ from: upstream, to: local }],
    });
  }

  function saveObservations() {
    save({ searchObservations: observations });
  }

  return (
    <Grid gutter="lg" align="stretch">
      <Grid.Col span={{ base: 12, lg: 6 }}>
        <Card h="100%">
          <Title order={5} mb="md">{t("eval_dimensions")}</Title>
          <Text size="sm" c="dimmed" mb="md">
            {t("eval_dimensions_desc")}
          </Text>
          <Stack gap="sm" mb="md">
            {dimensions.map((dim, i) => (
              <Card key={i} withBorder p="sm">
                <Group justify="space-between" mb="xs" wrap="nowrap" gap="xs">
                  <TextInput
                    size="xs"
                    variant="unstyled"
                    value={dim.name}
                    onChange={(e) => setDimName(i, e.currentTarget.value)}
                    onBlur={() => commitName(i)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    styles={{ input: { fontWeight: 500 } }}
                    style={{ flex: 1 }}
                  />
                  <CloseButton size="sm" onClick={() => removeDimension(i)} />
                </Group>
                <Textarea
                  size="xs"
                  autosize
                  minRows={2}
                  maxRows={6}
                  placeholder={t("requirement_placeholder")}
                  value={dim.description ?? ""}
                  onChange={(e) => setDimDescription(i, e.currentTarget.value)}
                  onBlur={() => commitDescription(i)}
                />
              </Card>
            ))}
            {dimensions.length === 0 && (
              <Text size="sm" c="dimmed">{t("no_dimensions")}</Text>
            )}
          </Stack>
          <Group gap="xs">
            <TextInput
              size="xs"
              placeholder={t("add_dimension_placeholder")}
              value={newDim}
              onChange={(e) => setNewDim(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && addDimension()}
              style={{ flex: 1 }}
            />
            <Button size="xs" onClick={addDimension} disabled={!newDim.trim()} loading={saving}>
              {t("add")}
            </Button>
          </Group>
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 6 }}>
        <Card h="100%">
          <Title order={5} mb="md">{t("search_observations")}</Title>
          <Text size="sm" c="dimmed" mb="md">
            {t("search_observations_desc")}
          </Text>
          <Textarea
            value={observations}
            onChange={(e) => setObservations(e.currentTarget.value)}
            rows={4}
            placeholder={t("search_observations_placeholder")}
            mb="sm"
          />
          <Button size="xs" onClick={saveObservations} loading={saving}>
            {t("save")}
          </Button>
        </Card>
      </Grid.Col>
    </Grid>
  );
}

export default function ProjectPipeline({ params }) {
  const { t, i18n } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [projectCandidates, setProjectCandidates] = useState([]);
  const [candidateMap, setCandidateMap] = useState({});
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [projectFiles, setProjectFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Active tab (controlled so the no-dimensions alert can jump to Settings)
  const [activeTab, setActiveTab] = useState("pipeline");
  const isMobile = useIsMobile();

  // Candidate detail drawer
  const [drawerPc, setDrawerPc] = useState(null);

  // Reject candidate modal
  const [rejectPc, setRejectPc] = useState(null);
  const [rejectInitiator, setRejectInitiator] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [rejectedExpanded, setRejectedExpanded] = useState(false);
  // On-hold candidates are still in play, so their section starts open (rejected starts closed).
  const [onHoldExpanded, setOnHoldExpanded] = useState(true);
  const [rejectedSearch, setRejectedSearch] = useState("");
  const [rejectedUnattributedOnly, setRejectedUnattributedOnly] = useState(false);
  const [editingReasonId, setEditingReasonId] = useState(null);
  const [editingReasonText, setEditingReasonText] = useState("");
  const [savingReasonId, setSavingReasonId] = useState(null);

  // Edit project (title, role title, briefing) modal
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const editForm = useForm({
    initialValues: { title: "", roleTitle: "", roleBriefing: "", language: "", status: "" },
    validate: { title: (v) => (v.trim() ? null : "Title is required") },
  });

  // Set in the Esc handler so the imminent onBlur knows to skip saving.
  // React clears focus → onBlur fires after Esc, which would otherwise persist
  // the in-progress edit instead of cancelling it.
  const cancelEditRef = useRef(false);

  // Add candidate modal
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  // Bulk-add from LinkedIn URLs modal
  const [bulkOpen, setBulkOpen] = useState(false);

  const [generateOpen, setGenerateOpen] = useState(false);

  function openGenerate() {
    setActiveTab("documents");
    setGenerateOpen(true);
  }

  useEffect(() => {
    loadData();
  }, [params.id]);

  async function loadData() {
    setLoading(true);
    try {
      const proj = await getProject(params.id);
      setProject(proj);

      // Fetch client, project candidates, generated docs, and project files in parallel
      const [cli, pcs, docs, files] = await Promise.all([
        proj.clientId ? getClient(proj.clientId).catch(() => null) : null,
        getProjectCandidates(params.id),
        getGeneratedDocuments(params.id).catch(() => []),
        getProjectFiles(params.id).catch(() => []),
      ]);
      setGeneratedDocs(docs ?? []);
      setProjectFiles(files ?? []);
      setClient(cli);
      const pcList = Array.isArray(pcs) ? pcs : [];
      setProjectCandidates(pcList);

      // Fetch candidate details for both project candidates and sources
      const candidateIds = [...new Set([
        ...pcList.map((pc) => pc.candidateId),
        ...(proj.industryExpertCandidateIds ?? []),
      ])];
      const candidates = await Promise.all(
        candidateIds.map((id) => getCandidate(id).catch(() => null))
      );
      const map = {};
      for (const c of candidates) {
        if (c) map[c.id] = c;
      }
      setCandidateMap(map);
    } catch (e) {
      console.error("Failed to load project", e);
    } finally {
      setLoading(false);
    }
  }

  async function handleStageChange(pc, newStage) {
    if (!newStage || newStage === pc.stage) return;
    try {
      const updated = await updateProjectCandidate(params.id, pc.id, { stage: newStage });
      setProjectCandidates((prev) =>
        prev.map((p) => (p.id === pc.id ? { ...p, stage: updated.stage ?? newStage } : p))
      );
      notifications.show({ message: t("saved"), color: "green", autoClose: 1500 });
    } catch (e) {
      notifications.show({ color: "red", title: t("error"), message: t("stage_update_failed") });
    }
  }

  function handleDragEnd(result) {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;
    const pc = projectCandidates.find((p) => p.id === draggableId);
    if (pc) handleStageChange(pc, destination.droppableId);
  }

  async function handleRejectConfirm() {
    if (!rejectPc || !rejectInitiator) return;
    setRejecting(true);
    try {
      if (rejectInitiator === "clerical") {
        await deleteProjectCandidate(params.id, rejectPc.id);
        setProjectCandidates((prev) => prev.filter((p) => p.id !== rejectPc.id));
        notifications.show({ title: t("removed"), message: t("clerical_removed_msg") });
      } else {
        const updated = await updateProjectCandidate(params.id, rejectPc.id, {
          stage: "rejected",
          rejectedBy: rejectInitiator,
          rejectionReason: rejectReason || null,
        });
        setProjectCandidates((prev) =>
          prev.map((p) => (p.id === rejectPc.id ? { ...p, ...updated } : p))
        );
      }
      setRejectPc(null);
      setRejectInitiator("");
      setRejectReason("");
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("reject_failed") });
    } finally {
      setRejecting(false);
    }
  }

  async function handleSetRejectedBy(pc, rejectedBy) {
    try {
      const updated = await updateProjectCandidate(params.id, pc.id, { rejectedBy });
      setProjectCandidates((prev) =>
        prev.map((p) => (p.id === pc.id ? { ...p, ...updated } : p))
      );
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("update_failed") });
    }
  }

  async function handleSaveReason(pc, nextReason) {
    const trimmed = (nextReason ?? "").trim();
    if (trimmed === (pc.rejectionReason ?? "").trim()) {
      setEditingReasonId(null);
      return;
    }
    setSavingReasonId(pc.id);
    try {
      const updated = await updateProjectCandidate(params.id, pc.id, { rejectionReason: trimmed });
      setProjectCandidates((prev) =>
        prev.map((p) => (p.id === pc.id ? { ...p, ...updated } : p))
      );
      setEditingReasonId(null);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("reason_save_failed") });
    } finally {
      setSavingReasonId(null);
    }
  }

  function openEditProject() {
    // "" represents "use tenant default" in the dropdown — distinct from
    // "de"/"en" overrides. Submit converts back to ClearLanguage / Language.
    const values = {
      title: project.title ?? "",
      roleTitle: project.roleTitle ?? "",
      roleBriefing: project.roleBriefing ?? "",
      language: project.language ?? "",
      status: project.status ?? "potential",
    };
    editForm.setValues(values);
    editForm.resetDirty(values);
    setEditOpen(true);
  }

  async function handleEditSubmit(values) {
    setSavingEdit(true);
    try {
      const patch = {
        title: values.title.trim(),
        roleTitle: values.roleTitle.trim(),
        roleBriefing: values.roleBriefing.trim(),
      };
      if (values.status && values.status !== project.status) patch.status = values.status;
      if (values.language === "") {
        if (project.language) patch.clearLanguage = true;
      } else if (values.language !== project.language) {
        patch.language = values.language;
      }
      const updated = await updateProject(params.id, patch);
      setProject(updated);
      setEditOpen(false);
      notifications.show({ title: t("saved"), message: t("project_updated"), color: "green" });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleHold(pc) {
    try {
      const updated = await updateProjectCandidate(params.id, pc.id, { stage: "onHold" });
      setProjectCandidates((prev) =>
        prev.map((p) => (p.id === pc.id ? { ...p, ...updated } : p))
      );
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("put_on_hold_failed") });
    }
  }

  // Back to the stage the candidate left — from the rejected or the on-hold section.
  async function handleRestore(pc) {
    const restoreStage = (pc.stage === "onHold" ? pc.onHoldAtStage : pc.rejectedAtStage) || "identified";
    try {
      const updated = await updateProjectCandidate(params.id, pc.id, { stage: restoreStage });
      setProjectCandidates((prev) =>
        prev.map((p) => (p.id === pc.id ? { ...p, ...updated } : p))
      );
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("restore_failed") });
    }
  }

  async function handleAddCandidate(candidateId) {
    setAdding(true);
    try {
      const pc = await addProjectCandidate(params.id, { candidateId });
      setProjectCandidates((prev) => [...prev, pc]);
      // Fetch candidate data if not already loaded
      if (!candidateMap[candidateId]) {
        const cand = await getCandidate(candidateId).catch(() => null);
        if (cand) setCandidateMap((prev) => ({ ...prev, [cand.id]: cand }));
      }
      setAddOpen(false);
      notifications.show({ title: t("candidate_added"), message: t("candidate_added_msg") });
    } catch (e) {
      notifications.show({ color: "red", title: t("error"), message: t("add_candidate_failed") });
    } finally {
      setAdding(false);
    }
  }

  async function handleAddSource(candidate) {
    const expertIds = project?.industryExpertCandidateIds ?? [];
    if (expertIds.includes(candidate.id)) return;
    try {
      const updated = await updateProject(params.id, {
        industryExpertCandidateIds: [...expertIds, candidate.id],
      });
      setProject(updated);
      setCandidateMap((prev) => ({ ...prev, [candidate.id]: candidate }));
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    }
  }

  async function handleRemoveSource(id) {
    const expertIds = project?.industryExpertCandidateIds ?? [];
    try {
      const updated = await updateProject(params.id, {
        industryExpertCandidateIds: expertIds.filter((x) => x !== id),
      });
      setProject(updated);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    }
  }

  async function handlePromoteSource(candidateId) {
    const expertIds = project?.industryExpertCandidateIds ?? [];
    const alreadyOnProject = projectCandidates.some((p) => p.candidateId === candidateId);
    try {
      if (!alreadyOnProject) {
        const pc = await addProjectCandidate(params.id, { candidateId });
        setProjectCandidates((prev) => [...prev, pc]);
      }
      const updated = await updateProject(params.id, {
        industryExpertCandidateIds: expertIds.filter((x) => x !== candidateId),
      });
      setProject(updated);
      notifications.show({ title: t("source_promoted"), message: t("source_promoted_msg") });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    }
  }

  async function uploadFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await fetch(`/api/v1/projects/${params.id}/files`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!resp.ok) throw new Error("Upload failed");
      const created = await resp.json();
      setProjectFiles((prev) => [created, ...prev]);
      notifications.show({ title: t("uploaded"), message: file.name, color: "green" });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("upload_failed") });
    } finally {
      setUploading(false);
    }
  }

  async function handleDropzone(files) {
    for (const file of files) {
      await uploadFile(file);
    }
  }

  async function handleDeleteProjectFile(fileId) {
    try {
      await deleteProjectFile(params.id, fileId);
      setProjectFiles((prev) => prev.filter((f) => f.id !== fileId));
      notifications.show({ title: t("deleted_notification"), message: t("file_removed"), color: "green" });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("delete_file_failed") });
    }
  }

  async function handleDeleteGeneratedDoc(docId) {
    try {
      await deleteGeneratedDocument(docId);
      setGeneratedDocs((prev) => prev.filter((d) => d.id !== docId));
      notifications.show({ title: t("deleted_notification"), message: t("document_removed"), color: "green" });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("delete_document_failed") });
    }
  }

  if (loading) {
    return <Center py="xl"><Loader /></Center>;
  }

  if (!project) {
    return <Text>{t("project_not_found")}</Text>;
  }

  // Group by stage, then A–Z within each column. Same-column drag is a no-op
  // (Ranking is a grade, not a position), so this does not fight a manual order.
  const byCandidate = (a, b) => compareCandidatesAlpha(
    candidateMap[a.candidateId] ?? { id: a.candidateId },
    candidateMap[b.candidateId] ?? { id: b.candidateId },
  );
  const pipelineByStage = {};
  const rejectedCandidates = [];
  const onHoldCandidates = [];
  for (const s of stageKeys) pipelineByStage[s.key] = [];
  for (const pc of projectCandidates) {
    const key = pc.stage ?? "identified";
    if (key === "rejected") {
      rejectedCandidates.push(pc);
    } else if (key === "onHold") {
      onHoldCandidates.push(pc);
    } else {
      if (!pipelineByStage[key]) pipelineByStage[key] = [];
      pipelineByStage[key].push(pc);
    }
  }
  for (const key of Object.keys(pipelineByStage)) pipelineByStage[key].sort(byCandidate);
  rejectedCandidates.sort(byCandidate);
  onHoldCandidates.sort(byCandidate);
  const sourceIds = sortIdsByCandidateAlpha(project.industryExpertCandidateIds ?? [], candidateMap);
  const unattributedRejected = rejectedCandidates.filter((pc) => !pc.rejectedBy);
  const unattributedCount = unattributedRejected.length;
  const unattributedOnly = rejectedUnattributedOnly && unattributedCount > 0;
  const rejectedSearchLower = rejectedSearch.trim().toLocaleLowerCase("de");
  const filteredRejected = (unattributedOnly ? unattributedRejected : rejectedCandidates)
    .filter((pc) =>
      !rejectedSearchLower
      || candidateAlphaHaystack(candidateMap[pc.candidateId]).includes(rejectedSearchLower)
    );

  // Candidate IDs already in this project
  const existingCandidateIds = projectCandidates.map((pc) => pc.candidateId);

  // One stage column of the pipeline board. Shared between the desktop Grid and the mobile
  // horizontal-scroll row so the DnD droppable/draggable markup stays identical.
  const renderStageColumn = (stage) => {
    const pcs = pipelineByStage[stage.key] ?? [];
    return (
      <Card p="xs" style={{ minHeight: 200 }}>
        <Group justify="space-between" mb="xs">
          <Badge color={stage.color} variant="light" size="xs">
            {t(stage.tKey)} ({pcs.length})
          </Badge>
        </Group>
        <Droppable droppableId={stage.key}>
          {(provided, snapshot) => (
            <Stack
              gap="xs"
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                minHeight: 60,
                borderRadius: "var(--mantine-radius-sm)",
                backgroundColor: snapshot.isDraggingOver ? "var(--mantine-color-blue-light)" : undefined,
                transition: "background-color 0.2s ease",
              }}
            >
              {pcs.map((pc, index) => (
                <Draggable key={pc.id} draggableId={pc.id} index={index}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <CandidateCard
                        pc={pc}
                        candidateData={candidateMap[pc.candidateId]}
                        onOpenDrawer={setDrawerPc}
                        onHold={handleHold}
                        onReject={setRejectPc}
                        dragHandleProps={provided.dragHandleProps}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {pcs.length === 0 && !snapshot.isDraggingOver && (
                <Text size="xs" c="dimmed" ta="center" py="xl">
                  {t("no_candidates_in_stage")}
                </Text>
              )}
            </Stack>
          )}
        </Droppable>
      </Card>
    );
  };

  return (
    <Stack gap="lg">
      <Group className={activeTab === "status" ? "search-status-screen-only" : undefined}>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={() => setLocation("/projects")}>
          {t("projects")}
        </Button>
      </Group>

      {/* Project Header */}
      <Card className={activeTab === "status" ? "search-status-screen-only" : undefined}>
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="sm" mb="xs">
              <Title order={3}>{project.title}</Title>
              <Badge color={projectStatusColor(project.status)}>
                {projectStatusLabel(t, project.status)}
              </Badge>
              <Tooltip label={t("edit_project")}>
                <ActionIcon variant="subtle" size="sm" onClick={openEditProject} aria-label={t("edit_project")}>
                  <IconPencil size={14} />
                </ActionIcon>
              </Tooltip>
              <InfoHint i18nKey="pipeline_stages_help" multiline width={320} />
            </Group>
            {project.roleTitle && (
              <Text size="sm" fw={500} mb="xs">{project.roleTitle}</Text>
            )}
            {project.roleBriefing && <CollapsibleBriefing text={project.roleBriefing} />}
            <Group gap="xl">
              {client && (
                <Group gap="xs" style={{ cursor: "pointer" }} onClick={() => setLocation(`/clients/${project.clientId}`)}>
                  <ThemeIcon variant="light" size="sm"><IconUser size={14} /></ThemeIcon>
                  <Text size="sm" td="underline">{client.name}</Text>
                </Group>
              )}
            </Group>
          </div>
        </Group>
      </Card>

      {(project.competencyDimensions ?? []).length === 0 && (
        <Alert
          className={activeTab === "status" ? "search-status-screen-only" : undefined}
          variant="light"
          color="yellow"
          icon={<IconAlertTriangle size={18} />}
          title={t("no_dimensions_alert_title")}
        >
          <Stack gap="sm" align="flex-start">
            <Text size="sm">{t("no_dimensions_alert_body")}</Text>
            <Button
              size="xs"
              color="yellow"
              leftSection={<IconPlus size={14} />}
              onClick={() => setActiveTab("settings")}
            >
              {t("no_dimensions_alert_cta")}
            </Button>
          </Stack>
        </Alert>
      )}

      <Tabs value={activeTab} onChange={setActiveTab}>
        <ScrollableTabsList>
          <Tabs.Tab value="pipeline" leftSection={<IconBriefcase size={14} />}>{t("pipeline")}</Tabs.Tab>
          <Tabs.Tab value="status" leftSection={<IconBuilding size={14} />}>{t("tab_status")}</Tabs.Tab>
          <Tabs.Tab value="documents" leftSection={<IconFolder size={14} />}>
            {t("documents")}{(generatedDocs.length + projectFiles.length) > 0 ? ` (${generatedDocs.length + projectFiles.length})` : ""}
          </Tabs.Tab>
          <Tabs.Tab value="tasks" leftSection={<IconListCheck size={14} />}>{t("tab_tasks")}</Tabs.Tab>
          <Tabs.Tab value="settings" leftSection={<IconSettings size={14} />}>{t("settings")}</Tabs.Tab>
        </ScrollableTabsList>

        <Tabs.Panel value="status" pt="md">
          {activeTab === "status" && (
            <ProjectStatusTab
              projectId={params.id}
              onOpenPerson={(candidateId) => {
                const pc = projectCandidates.find((p) => p.candidateId === candidateId);
                if (pc) setDrawerPc(pc);
                else setLocation(`/candidates/${candidateId}`);
              }}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="pipeline" pt="md">
          <Stack gap="lg">
            <Group justify="flex-end">
              <Button
                size="xs"
                variant="light"
                leftSection={<IconFileText size={14} />}
                onClick={openGenerate}
              >
                {t("generate_document_btn")}
              </Button>
              <Menu shadow="md" position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button size="xs" leftSection={<IconPlus size={14} />} rightSection={<IconChevronDown size={12} />}>
                    {t("add_candidate")}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconUserPlus size={14} />} onClick={() => setAddOpen(true)}>
                    {t("add_existing_candidate")}
                  </Menu.Item>
                  <Menu.Item leftSection={<IconPlus size={14} />} onClick={() => setBulkOpen(true)}>
                    {t("add_candidate_new")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>

            {/* Sources strip — industry experts, sometimes self-nominate into the pipeline */}
            <Card p="sm">
              <Group justify="space-between" mb="xs" wrap="nowrap">
                <Group gap="xs">
                  <Badge color="orange" variant="light" size="xs">
                    {t("sources")} ({(project.industryExpertCandidateIds ?? []).length})
                  </Badge>
                  <Text size="xs" c="dimmed">{t("sources_help")}</Text>
                </Group>
                <div style={{ minWidth: 240 }}>
                  <CandidatePicker
                    onSelect={handleAddSource}
                    excludeIds={[
                      ...(project.industryExpertCandidateIds ?? []),
                      ...projectCandidates.map((p) => p.candidateId),
                    ]}
                  />
                </div>
              </Group>
              {(project.industryExpertCandidateIds ?? []).length === 0 ? (
                <Text size="xs" c="dimmed" py="xs">{t("no_sources")}</Text>
              ) : (
                <Group gap="xs" wrap="wrap">
                  {sourceIds.map((id) => {
                    const c = candidateMap[id] ?? {};
                    const name = alphaDisplayName(c);
                    return (
                      <Group
                        key={id}
                        gap={6}
                        wrap="nowrap"
                        p="xs"
                        style={{
                          border: "1px solid var(--mantine-color-default-border)",
                          borderRadius: "var(--mantine-radius-sm)",
                          maxWidth: 260,
                        }}
                      >
                        <div
                          style={{ cursor: "pointer", minWidth: 0, flex: 1 }}
                          onClick={() => setLocation(`/candidates/${id}`)}
                        >
                          <Text size="xs" fw={500} truncate>{name || "Loading..."}</Text>
                          <RoleCompanyLine candidate={c} t={t} />
                        </div>
                        <Tooltip label={t("promote_source_to_candidate")} withinPortal>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="blue"
                            onClick={() => handlePromoteSource(id)}
                          >
                            <IconUserPlus size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <CloseButton size="sm" onClick={() => handleRemoveSource(id)} />
                      </Group>
                    );
                  })}
                </Group>
              )}
            </Card>

            <DragDropContext onDragEnd={handleDragEnd}>
              {isMobile ? (
                // Phone: a horizontal-scroll board keeps the kanban overview instead of six tall
                // stacked full-width columns. Fixed-width columns so ~1.5 show at once (scroll cue);
                // stage changes also work via each card's drawer, so no touch-drag is required.
                <Box
                  style={{
                    display: "flex",
                    gap: "var(--mantine-spacing-sm)",
                    overflowX: "auto",
                    alignItems: "flex-start",
                    paddingBottom: 4,
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {stageKeys.map((stage) => (
                    <Box key={stage.key} style={{ flex: "0 0 240px", maxWidth: 300 }}>
                      {renderStageColumn(stage)}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Grid>
                  {stageKeys.map((stage) => (
                    <Grid.Col key={stage.key} span={{ base: 12, sm: 6, lg: 2 }}>
                      {renderStageColumn(stage)}
                    </Grid.Col>
                  ))}
                </Grid>
              )}
            </DragDropContext>

            {/* On-hold candidates section — paused, neither in a stage column nor rejected */}
            {onHoldCandidates.length > 0 && (
              <Card p="sm" mt="md" maw={960}>
                <Group
                  gap="xs"
                  style={{ cursor: "pointer" }}
                  onClick={() => setOnHoldExpanded((v) => !v)}
                >
                  {onHoldExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                  <Text size="sm" fw={500} c="dimmed">
                    {t("stage_onHold")} ({onHoldCandidates.length})
                  </Text>
                </Group>
                {onHoldExpanded && (
                  <Stack gap="xs" mt="sm">
                    {onHoldCandidates.map((pc) => {
                      const candidate = candidateMap[pc.candidateId] ?? {};
                      return (
                        <Group key={pc.id} justify="space-between" wrap="nowrap" p="xs" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-sm)" }}>
                          <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                            <Avatar size="sm" radius="xl" color="yellow">
                              {candidate.name?.split(" ").map((n) => n[0]).join("").slice(0, 2) ?? "?"}
                            </Avatar>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <Text size="sm" fw={500} truncate>{alphaDisplayName(candidate) || "Unknown"}</Text>
                              <Group gap={6} mt={2}>
                                {pc.onHoldAtStage && (
                                  <Badge size="xs" variant="light" color="gray">
                                    {t("was_stage", { stage: t(`stage_${pc.onHoldAtStage}`) })}
                                  </Badge>
                                )}
                                {pc.onHoldAt && (
                                  <Text size="xs" c="dimmed">
                                    {t("on_hold_since", { date: new Date(pc.onHoldAt).toLocaleDateString() })}
                                  </Text>
                                )}
                              </Group>
                            </div>
                          </Group>
                          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                            <Button
                              size="xs" variant="light"
                              leftSection={<IconPlayerPlay size={14} />}
                              onClick={() => handleRestore(pc)}
                            >
                              {t("reactivate")}
                            </Button>
                            <Button
                              size="xs" variant="subtle"
                              onClick={() => setDrawerPc(pc)}
                            >
                              {t("details")}
                            </Button>
                          </Group>
                        </Group>
                      );
                    })}
                  </Stack>
                )}
              </Card>
            )}

            {/* Rejected candidates section */}
            {rejectedCandidates.length > 0 && (
              <Card p="sm" mt="md" maw={960}>
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Group
                      gap="xs"
                      style={{ cursor: "pointer" }}
                      onClick={() => setRejectedExpanded((v) => !v)}
                    >
                      {rejectedExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                      <Text size="sm" fw={500} c="dimmed">
                        {t("rejected")} ({rejectedCandidates.length})
                      </Text>
                    </Group>
                    {unattributedCount > 0 && (
                      <Chip
                        size="xs"
                        radius="sm"
                        checked={unattributedOnly}
                        onChange={(checked) => {
                          setRejectedUnattributedOnly(checked);
                          if (checked) setRejectedExpanded(true);
                        }}
                        variant={unattributedOnly ? "filled" : "outline"}
                      >
                        {t("rejected_filter_unattributed")} ({unattributedCount})
                      </Chip>
                    )}
                  </Group>
                  {rejectedExpanded && (
                    <TextInput
                      size="xs"
                      placeholder={t("search_rejected")}
                      value={rejectedSearch}
                      onChange={(e) => setRejectedSearch(e.currentTarget.value)}
                      rightSection={
                        rejectedSearch ? (
                          <CloseButton size="xs" onClick={() => setRejectedSearch("")} />
                        ) : null
                      }
                      style={{ maxWidth: 260, width: "100%" }}
                    />
                  )}
                </Group>
                {rejectedExpanded && (
                  <Stack gap="xs" mt="sm">
                    {filteredRejected.length === 0 ? (
                      <Text size="xs" c="dimmed" ta="center" py="md">
                        {t("no_rejected_match")}
                      </Text>
                    ) : (
                      filteredRejected.map((pc) => {
                        const candidate = candidateMap[pc.candidateId] ?? {};
                        const isEditing = editingReasonId === pc.id;
                        const isSaving = savingReasonId === pc.id;
                        return (
                          <Group key={pc.id} justify="space-between" wrap="nowrap" p="xs" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-sm)" }}>
                            <Group gap="sm" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                              <Avatar size="sm" radius="xl" color="blue">
                                {candidate.name?.split(" ").map((n) => n[0]).join("").slice(0, 2) ?? "?"}
                              </Avatar>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <Text size="sm" fw={500} truncate>{alphaDisplayName(candidate) || "Unknown"}</Text>
                                <Group gap={4} mt={2}>
                                  {pc.rejectedAtStage && (
                                    <Badge size="xs" variant="light" color="gray">
                                      {t("was_stage", { stage: t(`stage_${pc.rejectedAtStage}`) })}
                                    </Badge>
                                  )}
                                  <RejectedByBadge pc={pc} t={t} onSelect={handleSetRejectedBy} />
                                </Group>
                                {isEditing ? (
                                  <Textarea
                                    mt={4}
                                    size="xs"
                                    autosize
                                    minRows={1}
                                    maxRows={6}
                                    autoFocus
                                    disabled={isSaving}
                                    value={editingReasonText}
                                    placeholder={t("reject_notes_placeholder")}
                                    onChange={(e) => setEditingReasonText(e.currentTarget.value)}
                                    onBlur={() => {
                                      if (cancelEditRef.current) {
                                        cancelEditRef.current = false;
                                        return;
                                      }
                                      handleSaveReason(pc, editingReasonText);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Escape") {
                                        e.preventDefault();
                                        cancelEditRef.current = true;
                                        setEditingReasonId(null);
                                      } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                                        e.preventDefault();
                                        handleSaveReason(pc, editingReasonText);
                                      }
                                    }}
                                  />
                                ) : (
                                  <UnstyledButton
                                    mt={2}
                                    onClick={() => {
                                      setEditingReasonId(pc.id);
                                      setEditingReasonText(pc.rejectionReason ?? "");
                                    }}
                                    style={{ display: "block", width: "100%", textAlign: "left" }}
                                  >
                                    <Text
                                      size="xs"
                                      c={pc.rejectionReason ? "dimmed" : "orange"}
                                      fs={pc.rejectionReason ? "normal" : "italic"}
                                      style={{ whiteSpace: "pre-wrap" }}
                                    >
                                      {pc.rejectionReason || t("add_reason")}
                                    </Text>
                                  </UnstyledButton>
                                )}
                              </div>
                            </Group>
                            <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                              <Button
                                size="xs" variant="light"
                                leftSection={<IconRestore size={14} />}
                                onClick={() => handleRestore(pc)}
                              >
                                {t("restore")}
                              </Button>
                              <Button
                                size="xs" variant="subtle"
                                onClick={() => setDrawerPc(pc)}
                              >
                                {t("details")}
                              </Button>
                            </Group>
                          </Group>
                        );
                      })
                    )}
                  </Stack>
                )}
              </Card>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="documents" pt="md">
          <Stack gap="lg">
            <Grid gutter="lg" align="flex-start">
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Stack gap="xs">
                  <Group justify="space-between" wrap="nowrap">
                    <Text size="sm" fw={500}>{t("generated_documents")}</Text>
                    <Button
                      size="xs"
                      leftSection={<IconFileText size={14} />}
                      onClick={() => setGenerateOpen(true)}
                    >
                      {t("generate_document_btn")}
                    </Button>
                  </Group>
                  <GeneratedDocumentsList docs={generatedDocs} onDelete={handleDeleteGeneratedDoc} />
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Stack gap="xs">
                  <Text size="sm" fw={500}>{t("project_files")}</Text>
                  <Dropzone
                    onDrop={handleDropzone}
                    loading={uploading}
                    multiple
                    p="md"
                  >
                    <Group justify="center" gap="md" style={{ pointerEvents: "none" }}>
                      <Dropzone.Accept>
                        <IconUpload size={32} stroke={1.5} color="var(--mantine-color-blue-6)" />
                      </Dropzone.Accept>
                      <Dropzone.Reject>
                        <IconX size={32} stroke={1.5} color="var(--mantine-color-red-6)" />
                      </Dropzone.Reject>
                      <Dropzone.Idle>
                        <IconUpload size={32} stroke={1.5} color="var(--mantine-color-dimmed)" />
                      </Dropzone.Idle>
                      <div>
                        <Text size="sm" fw={500}>{t("dropzone_title")}</Text>
                        <Text size="xs" c="dimmed">{t("dropzone_hint")}</Text>
                      </div>
                    </Group>
                  </Dropzone>
                  {projectFiles.map((f) => (
                    <Card key={f.id} p="sm">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                          <ThemeIcon variant="light" size="md" color="blue">
                            <IconFileText size={16} />
                          </ThemeIcon>
                          <div style={{ minWidth: 0 }}>
                            <Text size="sm" fw={500} truncate>{f.fileName}</Text>
                            <Text size="xs" c="dimmed">
                              {(f.fileSize / 1024).toFixed(0)} KB &middot; {formatDateTime(f.uploadedAt, i18n.language)}
                            </Text>
                          </div>
                        </Group>
                        <Group gap="xs" wrap="nowrap">
                          <Button
                            variant="light" size="xs"
                            leftSection={<IconDownload size={14} />}
                            onClick={() => downloadFile(`projects/${params.id}/files/${f.id}/download`)}
                          >
                            {t("download")}
                          </Button>
                          <Button
                            variant="subtle" size="xs" color="red"
                            onClick={() => handleDeleteProjectFile(f.id)}
                          >
                            <IconTrash size={14} />
                          </Button>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                  {projectFiles.length === 0 && (
                    <Text size="sm" c="dimmed">{t("no_files_uploaded")}</Text>
                  )}
                </Stack>
              </Grid.Col>
            </Grid>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="tasks" pt="md">
          <TasksPanel projectId={project.id} />
        </Tabs.Panel>

        <Tabs.Panel value="settings" pt="md">
          <ProjectSettings project={project} onUpdate={setProject} />
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={generateOpen}
        onClose={() => setGenerateOpen(false)}
        title={t("generate_document_btn")}
        size="xl"
        centered
        fullScreen={isMobile}
        keepMounted
      >
        {project && (
          <DocumentGenerator
            projectId={params.id}
            lockProject
            projectCandidates={projectCandidates}
            candidateMap={candidateMap}
            onGenerated={() => {
              getGeneratedDocuments(params.id).then((docs) => setGeneratedDocs(docs ?? []));
              setGenerateOpen(false);
            }}
          />
        )}
      </Modal>

      {/* Add Candidate Modal */}
      <Modal opened={addOpen} onClose={() => setAddOpen(false)} title={t("add_candidate_to_project")}>
        <CandidatePicker
          onSelect={(c) => handleAddCandidate(c.id)}
          excludeIds={existingCandidateIds}
          disabled={adding}
          size="sm"
          autoFocus
        />
      </Modal>

      {/* Unified add-candidate dialog (files / LinkedIn URLs / typed), scoped to this project */}
      <AddCandidateDialog
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        project={{ id: params.id, title: project.title }}
        onCreated={() => loadData()}
      />

      {/* Reject Candidate Modal */}
      <Modal
        opened={!!rejectPc}
        onClose={() => { setRejectPc(null); setRejectInitiator(""); setRejectReason(""); }}
        title={t("reject_candidate")}
        size="sm"
      >
        <Stack>
          <Select
            label={t("reject_reason")}
            placeholder={t("reject_who_initiated")}
            data={[
              { value: "candidate", label: t("rejection_candidate") },
              { value: "consultant", label: t("rejection_consultant") },
              { value: "client", label: t("rejection_client") },
              { value: "clerical", label: t("rejection_clerical") },
            ]}
            value={rejectInitiator}
            onChange={setRejectInitiator}
          />
          {rejectInitiator === "clerical" ? (
            <Text size="sm" c="dimmed">{t("rejection_clerical_help")}</Text>
          ) : (
            <Textarea
              label={t("reject_notes")}
              placeholder={t("reject_notes_placeholder")}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.currentTarget.value)}
              rows={3}
            />
          )}
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => { setRejectPc(null); setRejectInitiator(""); setRejectReason(""); }}>
              {t("cancel")}
            </Button>
            <Button color="red" onClick={handleRejectConfirm} loading={rejecting} disabled={!rejectInitiator}>
              {rejectInitiator === "clerical" ? t("remove_from_project") : t("reject")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={editOpen} onClose={() => setEditOpen(false)} title={t("edit_project")}>
        <form onSubmit={editForm.onSubmit(handleEditSubmit)}>
          <Stack>
            <TextInput
              label={t("project_title")}
              placeholder={t("project_title_placeholder")}
              required
              {...editForm.getInputProps("title")}
            />
            <TextInput
              label={t("role_title")}
              placeholder={t("title_placeholder")}
              {...editForm.getInputProps("roleTitle")}
            />
            <Textarea
              label={t("role_briefing")}
              placeholder={t("role_briefing_placeholder")}
              autosize
              minRows={4}
              maxRows={12}
              {...editForm.getInputProps("roleBriefing")}
            />
            <Select
              label={t("project_status")}
              data={projectStatusOptions(t)}
              allowDeselect={false}
              {...editForm.getInputProps("status")}
            />
            <Select
              label={t("output_language")}
              description={t("output_language_help")}
              data={[
                { value: "", label: t("use_tenant_default") },
                { value: "de", label: "Deutsch" },
                { value: "en", label: "English" },
              ]}
              {...editForm.getInputProps("language")}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditOpen(false)}>{t("cancel")}</Button>
              <Button type="submit" loading={savingEdit}>{t("save")}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <ProjectCandidateDrawer
        opened={!!drawerPc}
        onClose={() => setDrawerPc(null)}
        projectCandidate={drawerPc}
        candidate={drawerPc ? candidateMap[drawerPc.candidateId] : null}
        project={project}
        onUpdate={(updated) => {
          setProjectCandidates((prev) =>
            prev.map((p) => (p.id === updated.id ? updated : p))
          );
          setDrawerPc(updated);
        }}
        onProjectUpdate={setProject}
        onNavigate={(cId) => setLocation(`/candidates/${cId}`)}
      />
    </Stack>
  );
}
